import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Checkbox, Text, Colors } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useUserMultiRegionSelection } from 'state/dashboard';
import { Region, METROPOLITAN_CODE_TO_LABEL, State } from 'utils/constants/location';

const checkboxContainerStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 'padding_12',
  marginTop: 'padding_16',
};

const checkboxItemStyle = {
  padding: 'padding_12',
  borderRadius: '4px',
  border: `1px solid ${Colors.ArmyGreen200}`,
  _hover: {
    background: Colors.ArmyGreen100,
  },
};

type RegionSelectionStepProps = {
  selectedState: string | null;
  selectedRegions: string[];
  onRegionsChange: (regions: string[]) => void;
};

const RegionSelectionStep = ({
  selectedState,
  selectedRegions,
  onRegionsChange,
}: RegionSelectionStepProps) => {
  const { availableStates } = useAvailableCities();
  const [, setRegions] = useUserMultiRegionSelection();

  const regionOptions = useMemo(() => {
    if (!selectedState || !availableStates[selectedState as State]) {
      return [];
    }

    const regionsForState = availableStates[selectedState as State];

    return Object.keys(regionsForState.regions).map((key: string) => ({
      id: key,
      value: key,
      label: METROPOLITAN_CODE_TO_LABEL[key] || key,
    }));
  }, [availableStates, selectedState]);

  const handleRegionToggle = (regionId: string) => {
    const newRegions = selectedRegions.includes(regionId)
      ? selectedRegions.filter((r) => r !== regionId)
      : [...selectedRegions, regionId];

    onRegionsChange(newRegions);
    setRegions(newRegions as Region[]);
  };

  const handleSelectAll = () => {
    const allRegionIds = regionOptions.map((r) => r.value);
    if (selectedRegions.length === allRegionIds.length) {
      onRegionsChange([]);
      setRegions([]);
    } else {
      onRegionsChange(allRegionIds);
      setRegions(allRegionIds as Region[]);
    }
  };

  const allSelected =
    regionOptions.length > 0 && selectedRegions.length === regionOptions.length;

  return (
    <SectionWrapper>
      <Header
        title="Select Regions"
        description={`Choose the regions within ${
          selectedState || 'your state'
        } where you want to see projects.`}
      />
      {regionOptions.length > 0 && (
        <FlexColumn sx={{ gap: 'padding_8' }}>
          <Checkbox isChecked={allSelected} onToggle={handleSelectAll}>
            <Text.SmallLabel>Select all</Text.SmallLabel>
          </Checkbox>
          <Box sx={checkboxContainerStyle}>
            {regionOptions.map((region) => (
              <Box key={region.id} sx={checkboxItemStyle}>
                <Checkbox
                  isChecked={selectedRegions.includes(region.value)}
                  onToggle={() => handleRegionToggle(region.value)}
                >
                  <Text.Small>{region.label}</Text.Small>
                </Checkbox>
              </Box>
            ))}
          </Box>
        </FlexColumn>
      )}
    </SectionWrapper>
  );
};

export default RegionSelectionStep;
